import {
  languageDisplayCode,
  languageDisplayLabel,
  LanguagesEnum,
} from '@/api/enums';
import { Account } from '@/client/accounts';
import {
  ContentSeries,
  CourseAvailabilityOptionsEnum,
  CourseEntityScheduleEnum,
  CourseFilesType,
  CourseStatus,
  CourseStatusAttemptEnum,
  CourseTranslationsType,
  CourseUser,
  MaterialType,
  ModifiedCourseFiles,
  ModifiedCourseTranslationsType,
  PosterCategory,
  Resource,
  ResourceTranslationPayload,
  ResourceType,
} from '@/client/courses';
import {
  ContentItemType,
  Course,
  CourseContent,
  CourseContentType,
  ModifiedResource,
  ModifiedResourceTranslations,
} from '@/client/courses/types/courses.type';
import { ScopeMode } from '@/client/custom-roles';
import { File, FileExtensions } from '@/client/files';
import { PlannerType } from '@/client/planner/types';
import {
  AnswerOption,
  ContentTranslation,
  ModifiedAnswerOption,
  ModifiedContentTranslations,
  ModifiedQuestion,
  Question,
  QuestionType,
  Quiz,
} from '@/client/quizzes';
import { Tag } from '@/client/tags';
import { Actions, Subjects, SystemRoles, User } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { availableLanguages } from '@/common/constants/languages';
import { TranslationFunctionType } from '@/common/types';
import { usePermission } from '@/hooks/usePermission';
import { branchAdminCheck } from '@/utils/helpers/account.helper';
import { decodeHTML } from '@/utils/helpers/display.helper';
import { t } from 'i18next';
import moment from 'moment';
import { object, string } from 'yup';

export const documentContentTypeOptions = (t: TranslationFunctionType) => [
  {
    label: t('material.type.customMaterial'),
    value: MaterialType.CUSTOM_MATERIAL,
  },
  {
    label: t('material.type.resource'),
    value: MaterialType.RESOURCE,
  },
];

export const courseAvailabilityOptions = (t: TranslationFunctionType) => [
  {
    label: t('courses.availability.markedAsAuto'),
    value: CourseAvailabilityOptionsEnum.AUTO,
  },
  {
    label: t('courses.availability.custom'),
    value: CourseAvailabilityOptionsEnum.CUSTOM,
  },
];

export const coursesEnrollToOptions = (
  t: TranslationFunctionType,
  currentUser?: User | null,
  currentAccount?: Account | null,
  visibleForBranchAdmin = false,
) => {
  const { can } = usePermission();

  const isBranchAdmin =
    currentUser &&
    currentAccount &&
    branchAdminCheck(currentUser, currentAccount);
  const scopeMode = currentUser?.customRole?.scopeMode;
  const isScopeRestricted =
    scopeMode === ScopeMode.GROUPS || scopeMode === ScopeMode.MANAGER;

  return [
    ...((isBranchAdmin && !visibleForBranchAdmin) || isScopeRestricted
      ? []
      : [
          {
            label: t('account'),
            value: CourseEntityScheduleEnum.ACCOUNT,
          },
        ]),
    ...(isScopeRestricted
      ? []
      : [
          {
            label: t('branches'),
            value: CourseEntityScheduleEnum.BRANCH,
          },
        ]),
    ...(can(Actions.UPDATE, Subjects.COURSE_GROUP_SCHEDULES) &&
    !isBranchAdmin &&
    scopeMode !== ScopeMode.MANAGER
      ? [
          {
            label: t('groups'),
            value: CourseEntityScheduleEnum.GROUP,
          },
        ]
      : []),
    {
      label: t('users'),
      value: CourseEntityScheduleEnum.USER,
    },
  ];
};

export const inheritedEntityType = (t: TranslationFunctionType) => [
  {
    label: t('account'),
    value: CourseEntityScheduleEnum.ACCOUNT,
  },
  {
    label: t('branch'),
    value: CourseEntityScheduleEnum.BRANCH,
  },
];

export const getScheduleEntity = (source: CourseEntityScheduleEnum) => {
  switch (source) {
    case CourseEntityScheduleEnum.ACCOUNT:
      return t('account');
    case CourseEntityScheduleEnum.BRANCH:
      return t('branch');
    case CourseEntityScheduleEnum.GROUP:
      return t('group');
    case CourseEntityScheduleEnum.USER:
      return t('user');
  }
};

export const courseLanguagesOptions = [
  {
    name: languageDisplayLabel[LanguagesEnum.ZH],
    label: languageDisplayCode[LanguagesEnum.ZH],
    value: LanguagesEnum.ZH,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.CZ],
    label: languageDisplayCode[LanguagesEnum.CZ],
    value: LanguagesEnum.CZ,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.DK],
    label: languageDisplayCode[LanguagesEnum.DK],
    value: LanguagesEnum.DK,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.NL],
    label: languageDisplayCode[LanguagesEnum.NL],
    value: LanguagesEnum.NL,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.EN],
    label: languageDisplayCode[LanguagesEnum.EN],
    value: LanguagesEnum.EN,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.FI],
    label: languageDisplayCode[LanguagesEnum.FI],
    value: LanguagesEnum.FI,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.FR],
    label: languageDisplayCode[LanguagesEnum.FR],
    value: LanguagesEnum.FR,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.DE],
    label: languageDisplayCode[LanguagesEnum.DE],
    value: LanguagesEnum.DE,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.KL],
    label: languageDisplayCode[LanguagesEnum.KL],
    value: LanguagesEnum.KL,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.IT],
    label: languageDisplayCode[LanguagesEnum.IT],
    value: LanguagesEnum.IT,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.LV],
    label: languageDisplayCode[LanguagesEnum.LV],
    value: LanguagesEnum.LV,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.LT],
    label: languageDisplayCode[LanguagesEnum.LT],
    value: LanguagesEnum.LT,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.NO],
    label: languageDisplayCode[LanguagesEnum.NO],
    value: LanguagesEnum.NO,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.PL],
    label: languageDisplayCode[LanguagesEnum.PL],
    value: LanguagesEnum.PL,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.PG],
    label: languageDisplayCode[LanguagesEnum.PG],
    value: LanguagesEnum.PG,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.ES],
    label: languageDisplayCode[LanguagesEnum.ES],
    value: LanguagesEnum.ES,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.SE],
    label: languageDisplayCode[LanguagesEnum.SE],
    value: LanguagesEnum.SE,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.TR],
    label: languageDisplayCode[LanguagesEnum.TR],
    value: LanguagesEnum.TR,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.VI],
    label: languageDisplayCode[LanguagesEnum.VI],
    value: LanguagesEnum.VI,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.AR],
    label: languageDisplayCode[LanguagesEnum.AR],
    value: LanguagesEnum.AR,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.UA],
    label: languageDisplayCode[LanguagesEnum.UA],
    value: LanguagesEnum.UA,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.RO],
    label: languageDisplayCode[LanguagesEnum.RO],
    value: LanguagesEnum.RO,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.BG],
    label: languageDisplayCode[LanguagesEnum.BG],
    value: LanguagesEnum.BG,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.EL],
    label: languageDisplayCode[LanguagesEnum.EL],
    value: LanguagesEnum.EL,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.ET],
    label: languageDisplayCode[LanguagesEnum.ET],
    value: LanguagesEnum.ET,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.HE],
    label: languageDisplayCode[LanguagesEnum.HE],
    value: LanguagesEnum.HE,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.HU],
    label: languageDisplayCode[LanguagesEnum.HU],
    value: LanguagesEnum.HU,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.ID],
    label: languageDisplayCode[LanguagesEnum.ID],
    value: LanguagesEnum.ID,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.JA],
    label: languageDisplayCode[LanguagesEnum.JA],
    value: LanguagesEnum.JA,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.KO],
    label: languageDisplayCode[LanguagesEnum.KO],
    value: LanguagesEnum.KO,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.RU],
    label: languageDisplayCode[LanguagesEnum.RU],
    value: LanguagesEnum.RU,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.SK],
    label: languageDisplayCode[LanguagesEnum.SK],
    value: LanguagesEnum.SK,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.SL],
    label: languageDisplayCode[LanguagesEnum.SL],
    value: LanguagesEnum.SL,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.TH],
    label: languageDisplayCode[LanguagesEnum.TH],
    value: LanguagesEnum.TH,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.BR],
    label: languageDisplayCode[LanguagesEnum.BR],
    value: LanguagesEnum.BR,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.HR],
    label: languageDisplayCode[LanguagesEnum.HR],
    value: LanguagesEnum.HR,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.SR],
    label: languageDisplayCode[LanguagesEnum.SR],
    value: LanguagesEnum.SR,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.MS],
    label: languageDisplayCode[LanguagesEnum.MS],
    value: LanguagesEnum.MS,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.HI],
    label: languageDisplayCode[LanguagesEnum.HI],
    value: LanguagesEnum.HI,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.SQ],
    label: languageDisplayCode[LanguagesEnum.SQ],
    value: LanguagesEnum.SQ,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.BA],
    label: languageDisplayCode[LanguagesEnum.BA],
    value: LanguagesEnum.BA,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.MK],
    label: languageDisplayCode[LanguagesEnum.MK],
    value: LanguagesEnum.MK,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.IS],
    label: languageDisplayCode[LanguagesEnum.IS],
    value: LanguagesEnum.IS,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.LB],
    label: languageDisplayCode[LanguagesEnum.LB],
    value: LanguagesEnum.LB,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.TL],
    label: languageDisplayCode[LanguagesEnum.TL],
    value: LanguagesEnum.TL,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.TA],
    label: languageDisplayCode[LanguagesEnum.TA],
    value: LanguagesEnum.TA,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.BN],
    label: languageDisplayCode[LanguagesEnum.BN],
    value: LanguagesEnum.BN,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.FA],
    label: languageDisplayCode[LanguagesEnum.FA],
    value: LanguagesEnum.FA,
  },
  {
    name: languageDisplayLabel[LanguagesEnum.UR],
    label: languageDisplayCode[LanguagesEnum.UR],
    value: LanguagesEnum.UR,
  },
];

export const getTranslationByLanguage = (
  translations: CourseTranslationsType[],
  language: LanguagesEnum,
  fallbackLang?: LanguagesEnum,
) => {
  const translation = translations.find(
    (object) => object.language === language,
  );

  // Fallback to default course language for missing translations
  const fallbackTranslation = translations.find(
    (object) => object.language === (fallbackLang || LanguagesEnum.EN),
  );

  return {
    name: translation?.name || fallbackTranslation?.name,
    description: translation?.description || fallbackTranslation?.description,
  };
};

export const getCourseName = (
  course: Course | undefined,
  currentUser: User | null,
) =>
  getTranslationByLanguage(
    course?.courseTranslations ?? [],
    currentUser?.language
      ? (currentUser?.language as LanguagesEnum)
      : course?.defaultLanguage || LanguagesEnum.EN,
    course?.defaultLanguage,
  )?.name;

export const priorityLanguagesOrder = [
  languageDisplayCode[LanguagesEnum.EN],
  languageDisplayCode[LanguagesEnum.DK],
  languageDisplayCode[LanguagesEnum.SE],
  languageDisplayCode[LanguagesEnum.NO],
];

export const sortLanguagesWithPriority = (languages: string[]) => {
  return languages?.sort((a, b) => {
    const priorityA = priorityLanguagesOrder.indexOf(a);
    const priorityB = priorityLanguagesOrder.indexOf(b);

    if (priorityA !== -1 || priorityB !== -1) {
      if (priorityB === -1) return -1;
      if (priorityA === -1) return 1;

      return priorityA - priorityB;
    }

    return a.localeCompare(b);
  });
};

export const getCourseLanguages = (languages: string[]) => {
  const sortedLanguages = sortLanguagesWithPriority(
    languages?.map((languageCode: string) => languageDisplayCode[languageCode]),
  );
  return sortedLanguages?.join(', ') || '';
};

export const getTranslatedCourseStatusAttempt = (
  status: CourseStatusAttemptEnum | undefined,
) => {
  switch (status) {
    case CourseStatusAttemptEnum.IN_PROGRESS: {
      return t('course.attempt.status.inProgress');
    }
    case CourseStatusAttemptEnum.COMPLETED: {
      return t('course.attempt.status.completed');
    }
    case CourseStatusAttemptEnum.FORCE_COMPLETED: {
      return t('course.attempt.status.forceCompleted');
    }
    case CourseStatusAttemptEnum.FAILED: {
      return t('course.attempt.status.failed');
    }
    default: {
      return undefined;
    }
  }
};

export const getCourseAttemptProgress = (attempt: any) => {
  if (!attempt?.status) {
    return 0;
  }

  // progress_measure have a value between 0 and 1
  const progressMeasure = (attempt?.payload?.progress_measure || 0) * 100;

  return attempt.status !== CourseStatusAttemptEnum.COMPLETED
    ? progressMeasure
    : 100;
};

// This is how data comes from BE, but we transform it to make use of our validation schema
export const modifyCourseTranslations = (
  courseTranslations: CourseTranslationsType[],
) => {
  const modifiedTranslations = {} as any;

  courseTranslations.forEach((item) => {
    const { language, name, description } = item;
    modifiedTranslations[language] = { name, description };
  });

  return modifiedTranslations;
};

export const modifyCourseFiles = (courseFiles: CourseFilesType[]) => {
  const modifiedFiles: ModifiedCourseFiles = [];

  const sortedCourseFiles = courseFiles.sort(
    (a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0),
  );

  sortedCourseFiles.forEach((item) => {
    const { language, file, fileMeta, priority } = item;

    const index = priority || 0;
    if (!modifiedFiles[index]) {
      modifiedFiles[index] = {
        translations: {},
      };
    }

    modifiedFiles[index].translations[language] = { file, fileMeta, priority };
  });

  return modifiedFiles;
};

// todo: add type
// TODO: This function will also serve the course files later
export const modifyCourseQuiz = (quiz: Quiz | undefined) => {
  if (!quiz) return null;

  const arrayToObject = (translationsArray: any) => {
    if (!Array.isArray(translationsArray)) return {};
    return translationsArray.reduce((acc, { language, ...rest }) => {
      acc[language] = { ...rest };
      return acc;
    }, {});
  };

  return {
    ...quiz,
    questions: quiz?.questions?.map((question: Question, index: number) => ({
      ...question,
      translations: arrayToObject(question.translations),
      answerOptions:
        question?.answerOptions?.map((option: any) => ({
          ...option,
          translations: arrayToObject(option.translations),
        })) || [],
      position: question.position || index,
      ...(question.label ? { label: question.label } : {}),
    })),
  };
};

export const parseModifiedCourseFiles = (
  modifiedFiles: ModifiedCourseFiles,
  data: { selfHosted: boolean; courseLanguages: string[] },
) => {
  const result: CourseFilesType[] = [];

  modifiedFiles.forEach((courseFile, index) => {
    Object.keys(courseFile.translations).forEach((language) => {
      const fileData = courseFile.translations[language];

      if (
        // Check if the language is available in the course for self hosted courses
        (data.selfHosted &&
          data.courseLanguages.includes(language) &&
          fileData?.file) ||
        (!data.selfHosted && fileData?.file) // Skip the previous check if its CC hosted
      ) {
        result.push({
          language,
          file: (fileData.file?.id ? fileData.file.id : null) as File | null,
          priority: index,
        });
      }
    });
  });

  return result;
};

export const sortAlphabetically = (a: any, b: any) => {
  if (a === '' || b === '') return;
  const firstLang = a.split('[')[1];
  const secondLang = b.split('[')[1];

  return firstLang.localeCompare(secondLang);
};

export const sortAlphabeticallyWithDKFirst = (languages: LanguagesEnum[]) => {
  const sortedLanguages = languages.sort((a, b) => {
    // Ensure da_DK is always first
    if (a === LanguagesEnum.DK) return -1;
    if (b === LanguagesEnum.DK) return 1;

    const aLabel = languageDisplayLabel[a];
    const bLabel = languageDisplayLabel[b];

    return sortAlphabetically(aLabel, bLabel);
  });

  return sortedLanguages;
};

export const isUserLanguageAvailableInCourse = (
  courseLanguages: string[],
  lang: LanguagesEnum,
) => courseLanguages?.includes(lang);

export const isUserLanguageAvailableinMaterial = (
  availableLanguages: LanguagesEnum[],
  lang: LanguagesEnum,
) => availableLanguages.includes(lang);

export const generateCourseTranslationsInitialValues = () => {
  return modifyCourseTranslations(
    availableLanguages.map((language) => ({
      language,
      name: '',
      description: '',
    })),
  );
};

export const parseModifiedCourseTranslations = (
  modifiedTranslations: ModifiedCourseTranslationsType,
) => {
  return Object.keys(modifiedTranslations).map((language) => ({
    language,
    name: modifiedTranslations[language]?.name || '',
    description: modifiedTranslations[language]?.description || '',
  }));
};

export const generateCourseTranslationsValidationSchema = (
  t: TranslationFunctionType,
) => {
  const schema = {} as any;

  availableLanguages.forEach((language) => {
    schema[language] = object().shape({
      name: string().nullable(),
      description: string().nullable(),
    });
  });

  return object()
    .shape(schema)
    .test(
      'at-least-one-title',
      t('error.title.required.atLeastOne'),
      (translations) => {
        if (!translations) return false;
        return availableLanguages.some(
          (lang) => !!translations[lang]?.name?.trim(),
        );
      },
    );
};

export const standardCourseLabelOptions = (
  t: TranslationFunctionType,
  isReleaseDateSet: boolean,
) => [
  {
    label: t('planner.type.onboardingPlan'),
    value: PlannerType.ONBOARDING_PLAN,
    disabled: isReleaseDateSet,
  },
  {
    label: t('account.trainingPlan'),
    value: PlannerType.TRAINING_PLAN,
  },
];

export const materialByTypeCreatePath = (type: MaterialType) =>
  RedirectPaths[
    type === MaterialType.RESOURCE
      ? RedirectPathsEnum.RESOURCES_CREATE
      : RedirectPathsEnum.POSTER_CREATE
  ]();

export const updateMaterialByTypePath = (type: MaterialType, id: string) =>
  RedirectPaths[
    type === MaterialType.RESOURCE
      ? RedirectPathsEnum.RESOURCES_EDIT
      : type === MaterialType.POSTER
      ? RedirectPathsEnum.POSTER_EDIT
      : RedirectPathsEnum.COURSES_CONTENT
  ](id);

export const materialByTypeListPath = (type: MaterialType) =>
  RedirectPaths[
    type === MaterialType.RESOURCE
      ? RedirectPathsEnum.RESOURCES
      : type === MaterialType.POSTER
      ? RedirectPathsEnum.POSTERS
      : RedirectPathsEnum.COURSES
  ]();

export const posterCategoriesOptions = (t: TranslationFunctionType) => [
  {
    label: t(`posters.${PosterCategory.COURSE_POSTERS}`),
    value: PosterCategory.COURSE_POSTERS,
  },
  {
    label: t(`posters.${PosterCategory.CYBERSECURITY}`),
    value: PosterCategory.CYBERSECURITY,
  },
  {
    label: t(`posters.${PosterCategory.GDPR}`),
    value: PosterCategory.GDPR,
  },
  {
    label: t(`posters.${PosterCategory.INFOGRAPHICS}`),
    value: PosterCategory.INFOGRAPHICS,
  },
  {
    label: t(`posters.${PosterCategory.INFO_SCREEN}`),
    value: PosterCategory.INFO_SCREEN,
  },
];

export const materialTypeOptions = (t: TranslationFunctionType) => [
  { label: t('posters'), value: MaterialType.POSTER },
  { label: t('resources'), value: MaterialType.RESOURCE },
  { label: t('custom.materials'), value: MaterialType.CUSTOM_MATERIAL },
];

export const filterFilesByLanguageAndExtension = (
  files: CourseFilesType[],
  language: LanguagesEnum,
  extensions: string[],
) =>
  files.filter((file) => {
    const matchesLanguage = file.language === language;
    const matchesExtension = extensions.includes(file.file?.extension || '');
    return matchesLanguage && matchesExtension;
  });

export const getContentItemType = (
  file: File | null,
): ContentItemType | undefined => {
  switch (file?.mimeType) {
    //   Mac/Linux: 'application/zip'
    //   Windows: 'application/x-zip-compressed'
    case 'application/zip':
    case 'application/x-zip-compressed': {
      return ContentItemType.SCORM;
    }
    case 'video/mp4': {
      return ContentItemType.VIDEO;
    }
    case 'application/pdf': {
      return ContentItemType.PDF;
    }
    default: {
      return undefined;
    }
  }
};

export const getFileType = (extension?: string) => {
  if (!extension) return undefined;

  for (const [type, extensions] of Object.entries(FileExtensions)) {
    if (extensions.includes(extension)) {
      return type as ContentItemType;
    }
  }
  return undefined;
};

const translationsArrayToObject = (translationsArray: ContentTranslation[]) => {
  if (!Array.isArray(translationsArray)) return {};

  return translationsArray.reduce((acc, { language, ...rest }) => {
    acc[language] = { ...rest };
    return acc;
  }, {} as ModifiedContentTranslations);
};

const translationsObjectToArray = (
  translationsObj: ModifiedContentTranslations,
  isSurvey?: boolean,
) => {
  if (!translationsObj || typeof translationsObj !== 'object') return [];

  return Object.entries(translationsObj)
    .filter(([, data]) => (isSurvey ? !!data.text : true))
    .map(([language, data]) => ({
      ...data,
      language,
    })) as ContentTranslation[];
};

const withMediaFileId = (translation: ContentTranslation) =>
  ({
    ...translation,
    media: translation.media?.file
      ? { ...translation.media, file: translation.media.file.id }
      : null,
  } as unknown as ContentTranslation);

const withoutClientKey = <T extends { clientKey?: string }>(
  item: T,
): Omit<T, 'clientKey'> => {
  const payload = { ...item };
  delete payload.clientKey;

  return payload;
};

export const convertQuestionsToArrayFormat = (
  questions: ModifiedQuestion[],
  isSurvey?: boolean,
  courseLanguages?: LanguagesEnum[],
): Question[] => {
  if (!questions?.length) return [];

  const filterByCourseLanguages = (translation: ContentTranslation) => {
    if (courseLanguages?.length) {
      return courseLanguages?.includes(translation.language);
    }
    return true;
  };

  return questions?.map(
    (question: ModifiedQuestion, questionIndex: number) => ({
      ...withoutClientKey(question),
      position: questionIndex,
      translations: translationsObjectToArray(question.translations, isSurvey)
        .filter(filterByCourseLanguages)
        .map(withMediaFileId),
      answerOptions: question?.answerOptions?.map(
        (option: ModifiedAnswerOption, optionIndex: number) => ({
          ...withoutClientKey(option),
          position: optionIndex,
          translations: translationsObjectToArray(option.translations).filter(
            filterByCourseLanguages,
          ),
        }),
      ),
      ...(question?.label && { label: question?.label }),
      type:
        question?.type && isSurvey
          ? question?.type
          : question?.answerOptions?.filter(
              (option: ModifiedAnswerOption) => option.isCorrect,
            ).length > 1
          ? QuestionType.MULTIPLE_CHOICE
          : QuestionType.SINGLE_CHOICE,
    }),
  );
};

export const getFullyTranslatedLanguages = (
  allLanguages: LanguagesEnum[],
  questions?: ModifiedQuestion[],
): string[] => {
  if (!questions?.length || !allLanguages?.length) {
    return [];
  }

  const questionsFormatted = convertQuestionsToArrayFormat(questions);

  return allLanguages
    .filter((language) => {
      return questionsFormatted.every((question: Question) => {
        const hasQuestionTranslation = question.translations.some(
          (translation: ContentTranslation) =>
            translation.language === language && translation.text,
        );

        const allOptionsHaveTranslation = question?.answerOptions?.every(
          (option: AnswerOption) =>
            option.translations.some(
              (translation: ContentTranslation) =>
                translation.language === language && translation.text,
            ),
        );

        return hasQuestionTranslation && allOptionsHaveTranslation;
      });
    })
    .map((language) => languageDisplayCode[language]);
};

const normalizeResourceTranslationsArray = (
  translations: unknown,
): ResourceTranslationPayload[] => {
  if (!Array.isArray(translations)) return [];
  return translations
    .filter(
      (entry) => !!entry && typeof entry === 'object' && 'language' in entry,
    )
    .map((entry) => ({
      ...entry,
      language: entry.language,
      file:
        entry.file && typeof entry.file === 'object' && 'id' in entry.file
          ? entry.file.id
          : entry.file,
    }));
};

export function convertFilesToArrayFormat(file: ModifiedResource): Resource;
export function convertFilesToArrayFormat(
  file: ModifiedResource | null | undefined,
): Resource | null;
export function convertFilesToArrayFormat(
  file: ModifiedResource | null | undefined,
): Resource | null {
  if (!file) return null;

  const translations = normalizeResourceTranslationsArray(
    Array.isArray(file.translations)
      ? file.translations
      : Object.entries<ModifiedResourceTranslations[string]>(
          file.translations,
        ).map(([language, data]) => ({
          ...data,
          language,
        })),
  );

  return {
    ...file,
    translations,
  } as unknown as Resource;
}

export const getFullyTranslatedLanguagesForFiles = (
  allLanguages: LanguagesEnum[],
  resource?: ModifiedResource | null,
): string[] => {
  if (!resource || !allLanguages?.length) {
    return [];
  }

  const fileFormatted = convertFilesToArrayFormat(resource);

  return allLanguages
    .filter((language) =>
      fileFormatted?.translations?.some(
        (translation) => translation.language === language && translation.file,
      ),
    )
    .map((language) => languageDisplayCode[language]);
};

export const hasAnyResourceTranslationFile = (
  resource?: ModifiedResource | null,
): boolean => {
  if (!resource?.translations) return false;
  if (Array.isArray(resource.translations)) {
    return (resource.translations as Array<{ file?: unknown }>).some(
      (translation) => !!translation?.file,
    );
  }
  return Object.values(
    resource.translations as Record<string, { file?: unknown }>,
  ).some((translation) => !!translation?.file);
};

export const modifyCourseResource = (
  resource: Resource,
): ModifiedResource | null => {
  if (!resource) return null;

  return {
    ...resource,
    translations: translationsArrayToObject(resource.translations),
  } as ModifiedResource;
};

export const getContentItemId = (
  item: string | { id?: string } | undefined,
) => {
  if (!item) return undefined;
  return typeof item === 'string' ? item : item.id;
};

export const filterValidSlideImagesForDisplay = (images: any[] | undefined) => {
  return images?.filter((image) => !!image.file?.signedUrl);
};

export const filterSlideImagesByTemplate = (
  images: any[] | undefined,
  templateType: string,
  galleryTemplateType: string,
) => {
  return images?.filter((image) =>
    templateType !== galleryTemplateType
      ? image.position === 0 && !!image.file?.signedUrl
      : !!image.file?.signedUrl,
  );
};

export const formatTags = (tags: Tag[]) => {
  return tags
    ?.map(
      (tag: Tag) =>
        tag.translations?.find((t) => t.language === LanguagesEnum.EN)?.name,
    )
    .join(', ');
};

/**
 * Single resolver for "which uploaded video actually plays for this language".
 * Every video-fallback question in the app routes through here, so the playback
 * chain and the banners that describe it can never disagree.
 *
 * Tiers, for VIDEO resources:
 *   1. Native translation for the chosen language
 *   2. Configured `resource.fallbackLanguage` (default EN if unset)
 *   3. English, when the configured fallback has no uploaded file
 *   4. Any uploaded file at all — a video that exists is always preferable to none
 *
 * Returns `null` only when the resource holds no uploaded file whatsoever (or when
 * it is non-VIDEO and the requested language has no upload).
 */
const resolvePlayableVideo = (
  resource: CourseContent['resource'],
  language: LanguagesEnum,
): { file: File; language: LanguagesEnum; isFallback: boolean } | null => {
  const translations = resource?.translations ?? [];

  const nativeTranslation = translations.find(
    (translation) => translation.language === language && translation.file,
  );

  if (nativeTranslation) {
    return { file: nativeTranslation.file, language, isFallback: false };
  }

  if (resource?.type !== ResourceType.VIDEO) {
    return null;
  }

  const configuredLanguage = resource.fallbackLanguage ?? LanguagesEnum.EN;
  const configuredTranslation = translations.find(
    (translation) =>
      translation.language === configuredLanguage && translation.file,
  );

  if (configuredTranslation) {
    return {
      file: configuredTranslation.file,
      language: configuredLanguage,
      isFallback: true,
    };
  }

  const englishTranslation = translations.find(
    (translation) =>
      translation.language === LanguagesEnum.EN && translation.file,
  );

  if (englishTranslation) {
    return {
      file: englishTranslation.file,
      language: LanguagesEnum.EN,
      isFallback: true,
    };
  }

  const anyTranslation = translations.find((translation) => translation.file);

  if (anyTranslation) {
    return {
      file: anyTranslation.file,
      language: anyTranslation.language,
      isFallback: true,
    };
  }

  return null;
};

export const getCurrentVideoFile = (
  module: CourseContent,
  language: LanguagesEnum,
): {
  file: File;
  isFallback: boolean;
  fallbackLanguage: LanguagesEnum | null;
} | null => {
  const resolved = resolvePlayableVideo(module.resource, language);

  if (!resolved) {
    return null;
  }

  return {
    file: resolved.file,
    isFallback: resolved.isFallback,
    fallbackLanguage: resolved.isFallback ? resolved.language : null,
  };
};

export const hasVideoFallback = (
  courseContents: CourseContent[] | undefined,
  language: LanguagesEnum,
): boolean => {
  if (!courseContents?.length) return false;

  const videoResources = courseContents.filter(
    (content) => content.resource?.type === ResourceType.VIDEO,
  );

  if (videoResources.length === 0) return false;

  return videoResources.some(
    (content) => resolvePlayableVideo(content.resource, language)?.isFallback,
  );
};

/**
 * Returns the effective fallback language for the first video that would fall
 * back when the learner picks {language}. Used to render the fallback banner copy.
 * Returns null when no video needs fallback (caller should not render the banner).
 */
export const getFirstVideoFallbackLanguage = (
  courseContents: CourseContent[] | undefined,
  language: LanguagesEnum,
): LanguagesEnum | null => {
  if (!courseContents?.length) return null;

  for (const content of courseContents) {
    if (content.resource?.type !== ResourceType.VIDEO) continue;

    const resolved = resolvePlayableVideo(content.resource, language);
    if (resolved?.isFallback) return resolved.language;
  }

  return null;
};

export const getVideoFallbackLanguages = (
  courseContents: CourseContent[] | undefined,
  targetLanguages: LanguagesEnum[],
): LanguagesEnum[] => {
  if (!courseContents?.length || !targetLanguages.length) return [];

  const videoResources = courseContents.filter(
    (content) => content.resource?.type === ResourceType.VIDEO,
  );

  if (videoResources.length === 0) return [];

  return targetLanguages.filter((language) =>
    videoResources.some(
      (content) => resolvePlayableVideo(content.resource, language)?.isFallback,
    ),
  );
};

export const getTargetLanguagesRequiringEnglish = (
  customizedContents: CourseContent[] | undefined,
  originalContents: CourseContent[] | undefined,
  targetLanguages: LanguagesEnum[],
): LanguagesEnum[] => {
  if (!originalContents?.length || !customizedContents?.length) return [];
  if (!targetLanguages.length) return [];
  if (targetLanguages.includes(LanguagesEnum.EN)) return [];

  const customizedVideos = customizedContents.filter(
    (content) => content.resource?.type === ResourceType.VIDEO,
  );
  if (customizedVideos.length === 0) return [];

  const customizedMissingEnglish = customizedVideos.some(
    (content) =>
      !(content.resource?.fullyTranslatedLanguages ?? []).includes(
        LanguagesEnum.EN,
      ),
  );
  if (!customizedMissingEnglish) return [];

  const originalVideos = originalContents.filter(
    (content) => content.resource?.type === ResourceType.VIDEO,
  );
  const originalHasEnglish = originalVideos.some((content) =>
    (content.resource?.fullyTranslatedLanguages ?? []).includes(
      LanguagesEnum.EN,
    ),
  );
  if (!originalHasEnglish) return [];

  return targetLanguages.filter((language) => {
    const inCustomized = customizedVideos.some((content) =>
      (content.resource?.fullyTranslatedLanguages ?? []).includes(language),
    );
    if (inCustomized) return false;
    const inOriginal = originalVideos.some((content) =>
      (content.resource?.fullyTranslatedLanguages ?? []).includes(language),
    );
    return !inOriginal;
  });
};

export const hasTranslationAccess = (
  currentAccountId: string,
  userRole: SystemRoles,
  course: Course,
  can: (action: Actions, subject: Subjects) => boolean,
  isSystemAccount = false,
) => {
  if (course.isStandardCatalogue && !isSystemAccount) {
    return false;
  }

  const isSuperUser = [
    SystemRoles.DEVELOPER,
    SystemRoles.SUPER_ADMIN,
    SystemRoles.COMMERCIAL_ADMIN,
  ].includes(userRole);

  const isAccountAdmin = [
    SystemRoles.ADMIN,
    SystemRoles.MULTIPLE_ACCOUNTS_ADMIN,
  ].includes(userRole);
  const ownsThisCourse = course.account?.id === currentAccountId;
  const canManageOwnCourse = isAccountAdmin && ownsThisCourse;

  const hasRequiredRole = isSuperUser || canManageOwnCourse;

  const isRetired = course?.status === CourseStatus.RETIRED;
  const isMaterial = course?.type !== MaterialType.COURSE;
  const isTranslatableContent = !isMaterial && !isRetired;
  const hasUpdatePermission = can(Actions.UPDATE, Subjects.COURSES);

  return hasRequiredRole && isTranslatableContent && hasUpdatePermission;
};

export const stripHtml = (html: string): string =>
  decodeHTML(
    html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, ''),
  ).trim();

export const SERIES_DURATION: Record<ContentSeries, number> = {
  [ContentSeries.ESSENTIALS]: 7,
  [ContentSeries.STORIES]: 3,
  [ContentSeries.THREAT_ALERTS]: 1,
};

export const NEW_COURSE_MONTHS = 6;

export const isUpcomingCourse = (releaseDate?: Date | string | null) =>
  !!getUpcomingReleaseDate(releaseDate);

export const isNewCourse = (releaseDate?: Date | string | null) =>
  !!releaseDate &&
  moment
    .utc(releaseDate)
    .local(true)
    .isAfter(moment().subtract(NEW_COURSE_MONTHS, 'months'), 'day') &&
  !isUpcomingCourse(releaseDate);

export const getLanguagesWithExistingTranslation = (
  courseContents: CourseContent[] | undefined,
  targetLanguages: LanguagesEnum[],
): LanguagesEnum[] => {
  if (!courseContents?.length || !targetLanguages.length) return [];

  const presentations = courseContents
    .filter((c) => !!c.presentation)
    .map((c) => c.presentation as NonNullable<typeof c.presentation>);

  if (presentations.length === 0) return [];

  return targetLanguages.filter((targetLang) =>
    presentations.some((presentation) =>
      presentation.translations.some(
        (t) => t.language === targetLang && t.slides.length > 0,
      ),
    ),
  );
};

export const getCourseContentTitle = (content?: CourseContent): string =>
  content?.quiz?.title ||
  content?.resource?.title ||
  content?.presentation?.title ||
  '';

export const isContentDeletableByStatus = ({
  status,
  isCustomCourse,
}: {
  status: CourseStatus;
  isCustomCourse: boolean;
}) => status !== CourseStatus.PUBLISHED || isCustomCourse;

export const canPreviewContent = (
  content?: {
    releaseDate?: Date | string | null;
    isPreviewable?: boolean | null;
  } | null,
  user?: User | null,
): boolean =>
  !content?.releaseDate ||
  !!content.isPreviewable ||
  moment(content.releaseDate).isSameOrBefore(moment(), 'day') ||
  !!user?.account?.isSystem;

/**
 * Release dates are stored at midnight UTC. Reading one with plain `moment()`
 * converts it to the viewer's local clock, which lands on the previous calendar
 * day anywhere west of UTC — and the date pickers compare local calendar
 * components only, so a pre-release day would be offered and the save rejected.
 * Reinterpreting the UTC wall clock as local keeps the calendar day intact.
 *
 * Returns null when the course is already released or has no release date.
 */
export const getUpcomingReleaseDate = (
  releaseDate?: Date | string | null,
): Date | null => {
  if (!releaseDate) {
    return null;
  }

  const release = moment.utc(releaseDate).local(true);

  return release.isAfter(moment(), 'day')
    ? release.startOf('day').toDate()
    : null;
};

/** The earliest date a course may be enrolled on: its release date, or today. */
export const getEnrollMinDate = (releaseDate?: Date | string | null): Date =>
  getUpcomingReleaseDate(releaseDate) ?? new Date();

export const containsScormModule = (modules: CourseContent[]): boolean => {
  return modules.some(
    (module) =>
      module.type === CourseContentType.RESOURCE &&
      module.resource?.type === ResourceType.SCORM,
  );
};

export const getCourseUserRowKey = (courseUser: CourseUser): string =>
  [
    courseUser.id,
    courseUser.enrollment?.id,
    courseUser.schedule?.id,
    courseUser.schedule?.date,
  ].join('-');

export enum ContentViewMode {
  MANAGE = 'manage',
  BROWSE = 'browse',
}

export const contentViewOptions = (t: TranslationFunctionType) => [
  {
    value: ContentViewMode.MANAGE,
    label: t('content.browse.toggle.manage'),
  },
  {
    value: ContentViewMode.BROWSE,
    label: t('content.browse.toggle.browse'),
  },
];
